import React, {useCallback} from 'react';

import {ResponsiveElementGroup} from 'src/components/responsive-element-group';
import {ServerResponseHandler} from 'src/components/server-response-handler';
import {Skeleton} from 'src/components/skeleton';
import {MAX_NUMBER_VALUE} from 'src/constants';
import {useLicenseCommonLimits} from 'src/hooks/dictionaries/useLicenseCommonLimits';

import {EllipsisTextHint} from '@pb/kit-component-ellipsis-text-hint';
import {FormSection} from '@pb/kit-component-form-section';
import {Group} from '@pb/kit-component-group';

import {LicenseLimitItem} from '../components/license-limit-item';
import {ResetDefaultLimitsSection} from '../components/reset-default-limits';

import {LicenseUsageLimitsSkeleton} from './license-usage-limits.skeleton';

import type {OnChangeLimits} from '../typedef';
import type {Error} from 'src/openapi/admin/models/Error';
import type {LicenseType} from 'src/openapi/admin/models/LicenseType';
import type {Limit} from 'src/openapi/admin/models/Limit';


type Props = {
    loading: boolean,
    loadingError: Error | null,

    usageLimits: Array<Limit>,
    defaultUsageLimits: Array<Limit>,
    isDefaultUsageLimits: boolean,

    onChange: React.Dispatch<React.SetStateAction<Array<Limit>>>,
    onFieldChange: (parameterName: string) => void,

    disabled?: boolean,
    licenseType?: LicenseType,
};

export const LicenseUsageLimitsView: React.FC<Props> = props => {
    const {
        loading,
        loadingError,
        usageLimits,
        defaultUsageLimits,
        isDefaultUsageLimits,
        onChange,
        disabled,
        licenseType,
        onFieldChange,
    } = props;
    const {licenseCommonLimits} = useLicenseCommonLimits();

    const onChangeLimit = useCallback<OnChangeLimits>((id, newLimit) => {
        onChange(limits => {
            if (limits.find(e => e.limitId === id)?.value !== newLimit) {
                onFieldChange?.('License Usage Limit');
            }

            return limits.map(limit => {
                if (limit.limitId !== id) {
                    return limit;
                }

                const unlimited = newLimit === undefined;
                return {...limit, value: unlimited ? MAX_NUMBER_VALUE : newLimit, unlimited};
            });
        });
    }, [onChange, onFieldChange]);

    const clearCustomLimit = useCallback((code: string) => {
        onChange(limits => {
            onFieldChange?.('License Usage Limit');
            return limits.map(limit => {
                if (limit.limitId !== code) {
                    return limit;
                }

                const value = defaultUsageLimits.find(({limitId}) => limitId === code)?.value;
                if (value === undefined) {
                    return limit;
                }

                const unlimited = value === MAX_NUMBER_VALUE;
                return {...limit, value, unlimited};
            });
        });
    }, [defaultUsageLimits, onChange, onFieldChange]);

    const onReset = useCallback(() => {
        onChange(defaultUsageLimits);
    }, [defaultUsageLimits, onChange]);

    return (
        <Skeleton view={<LicenseUsageLimitsSkeleton/>} shown={loading}>
            <ServerResponseHandler
                serverError={loadingError}
                smallView={true}
                sectionName="Usage Limits"
                showDefaultError={false}>
                <FormSection headline="Usage Limits">
                    <ResetDefaultLimitsSection
                        defaultUsageLimits={defaultUsageLimits}
                        isDefaultUsageLimits={isDefaultUsageLimits}
                        loading={loading}
                        licenseType={licenseType}
                        onReset={onReset}
                        disabled={disabled}
                    />
                    <br/>
                    <ResponsiveElementGroup>
                        {licenseCommonLimits.map(({description, children}) => (
                            <div key={description} className="pd-s">
                                <Group direction="v">
                                    <strong style={{width: 160}}>
                                        <EllipsisTextHint title={description}>
                                            {description}
                                        </EllipsisTextHint>
                                    </strong>
                                    {children?.map(limitDictionary => {
                                        const defaultValue = defaultUsageLimits?.find(
                                            ({limitId}) => limitId === limitDictionary.code,
                                        )?.value || 0;

                                        const customValue = usageLimits.find(
                                            ({limitId}) => limitId === limitDictionary.code,
                                        )?.value || 0;

                                        return (
                                            <LicenseLimitItem
                                                limitId={limitDictionary.code}
                                                key={limitDictionary.code}
                                                defaultValue={defaultValue}
                                                customValue={customValue}
                                                limitDictionary={limitDictionary}
                                                readonly={disabled}
                                                onChangeLimit={onChangeLimit}
                                                onClearCustomLimit={clearCustomLimit}
                                            />
                                        );
                                    })}
                                </Group>
                            </div>
                        ))}
                    </ResponsiveElementGroup>
                </FormSection>
            </ServerResponseHandler>
        </Skeleton>
    );
};
